const inputFormatters = new Map<string, Intl.DateTimeFormat>();

export function zonedInput(milliseconds: number, timeZone: string): string {
  let formatter = inputFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    });
    inputFormatters.set(timeZone, formatter);
  }
  const parts = Object.fromEntries(formatter.formatToParts(new Date(milliseconds)).map(p => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function scheduleInstant(value: string, timeZone: string): number {
  const wallTime = Date.parse(`${value}:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) || !Number.isFinite(wallTime) || new Date(wallTime).toISOString().slice(0, 16) !== value) {
    throw new Error("Enter a valid date and time.");
  }
  // Sample both sides of a clock change, then require an exact local-time match.
  const offsets = new Set([-36, -12, 0, 12, 36].map(hours => {
    const sample = wallTime + hours * 3600000;
    return Date.parse(`${zonedInput(sample, timeZone)}:00Z`) - sample;
  }));
  const matches = [...offsets].map(offset => wallTime - offset)
    .filter(instant => zonedInput(instant, timeZone) === value);
  if (!matches.length) throw new Error(`This time does not exist in ${timeZone} because the clocks move forward. Choose another time.`);
  if (matches.length > 1) throw new Error(`This time occurs twice in ${timeZone} because the clocks move back. Choose another time, or enter the exact time in UTC.`);
  return matches[0];
}

export function displayTime(seconds: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone, month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    hourCycle: "h23", timeZoneName: "shortOffset",
  }).format(new Date(seconds * 1000));
}

export const TIME_ZONE_KEY = "pads.gpuTimeZone";
export function savedTimeZone(): string {
  try {
    const saved = localStorage.getItem(TIME_ZONE_KEY);
    if (saved) { new Intl.DateTimeFormat("en", { timeZone: saved }); return saved; }
  } catch { /* Storage may be unavailable, or an old preference may be invalid. */ }
  return "Asia/Seoul";
}

export function availableTimeZones(selected: string): string[] {
  const common = ["Asia/Seoul", "Asia/Tokyo", "UTC", Intl.DateTimeFormat().resolvedOptions().timeZone, selected];
  const fallback = ["Asia/Singapore", "Asia/Kolkata", "Australia/Sydney", "Europe/London", "Europe/Berlin", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Pacific/Honolulu"];
  const zones = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : fallback;
  return [...new Set([...common, ...zones])];
}
